/** Os mesmos controles aparecem em linha ou em um menu quando a lista encolhe. */
import React, { useEffect, useRef, useState } from "react";
import { IconButton, Menu, MenuItem, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Check, MoreHorizontal } from "lucide-react";
import { i18n } from "../../translate/i18n";
import { commandMenuStyles } from "../../styles/commandMenu";

const MENU_ID = "tickets-toolbar-menu";
const useStyles = makeStyles((theme) => {
  const menu = commandMenuStyles(theme);
  const hover = menu.menuItem["&:hover"];
  return {
    ...menu,
    menuPaper: { ...menu.menuPaper, maxWidth: "calc(100vw - 32px)" },
    menu: { ...menu.menu, gap: 2 },
    menuItem: {
      ...menu.menuItem,
      minHeight: 0,
      lineHeight: 1.2,
      "&.Mui-selected": { backgroundColor: "transparent" },
      "&.Mui-selected:hover, &.Mui-focusVisible": hover,
    },
    menuIconBox: {
      ...menu.menuIconBox,
      "& svg": { width: 16, height: 16, strokeWidth: 1.8 },
    },
    check: {
      width: 16, flexShrink: 0, display: "inline-flex", color: theme.palette.primary.main,
    },
  };
});

const TicketsToolbarActions = ({ actions, compact, containerClassName, buttonClassName }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const restoreFocusRef = useRef(null);
  const label = i18n.t("tickets.toolbar.menu");
  const active = actions.some((action) => action.checked);

  useEffect(() => {
    if (!compact) {
      restoreFocusRef.current = null;
      setAnchorEl(null);
    }
  }, [compact]);

  const restoreTriggerFocus = () => {
    const trigger = restoreFocusRef.current;
    restoreFocusRef.current = null;
    if (trigger?.isConnected) trigger.focus();
  };
  const closeMenu = () => {
    restoreFocusRef.current = anchorEl;
    setAnchorEl(null);
  };
  const selectAction = (action) => {
    // Filtros e busca passam o foco ao modal/campo existente.
    restoreFocusRef.current = typeof action.checked === "boolean" ? anchorEl : null;
    setAnchorEl(null);
    action.onClick();
  };

  if (!compact) {
    return (
      <div className={containerClassName}>
        {actions.map((action) => (
          <Tooltip key={action.id} title={action.label} placement="bottom">
            <IconButton
              className={`${action.className || buttonClassName} ${action.checked ? "active" : ""}`}
              aria-label={action.label}
              aria-pressed={action.checked}
              onClick={action.onClick}
            >
              {action.icon}
            </IconButton>
          </Tooltip>
        ))}
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <Tooltip title={label} placement="bottom">
        <IconButton
          className={`${buttonClassName} ${active ? "active" : ""}`}
          aria-label={label}
          aria-haspopup="menu"
          aria-controls={anchorEl ? MENU_ID : undefined}
          aria-expanded={Boolean(anchorEl)}
          onClick={(event) => {
            restoreFocusRef.current = null;
            setAnchorEl(event.currentTarget);
          }}
        >
          <MoreHorizontal />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        variant="menu"
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
        transitionDuration={{ enter: 200, exit: 140 }}
        TransitionProps={{ onExited: restoreTriggerFocus }}
        PaperProps={{ className: classes.menuPaper }}
        MenuListProps={{ id: MENU_ID, "aria-label": label, className: classes.menu }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.id}
            className={classes.menuItem}
            role={typeof action.checked === "boolean" ? "menuitemcheckbox" : "menuitem"}
            aria-checked={action.checked}
            selected={Boolean(action.checked)}
            disableRipple
            onClick={() => selectAction(action)}
          >
            <span className={`${classes.menuIconBox} ${action.checked ? classes.menuIconBoxCobalt : classes.menuIconBoxSlate}`}>
              {action.menuIcon || action.icon}
            </span>
            <span className={classes.menuLabel}>{action.label}</span>
            <span className={classes.check}>{action.checked && <Check size={16} />}</span>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default TicketsToolbarActions;
